import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
} from '@mui/icons-material';

import {
  Alert,
  alertClasses,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import type { ActionFunctionArgs, MetaFunction } from '@remix-run/cloudflare';
import { json } from '@remix-run/cloudflare';
import { Form, useActionData, useNavigation } from '@remix-run/react';

import type React from 'react';
import { COLORS } from '@tbd/life-tokens';
import { TextButton } from '@tbd/lift-ui/components/TextButton';

import { createMinimalClient } from '#app/simcapture/index.js';

export const meta: MetaFunction = () => {
  return [{ title: 'LIFT - Reset password' }];
};

const CLOSE_ACTION = 'close';

// would love to have these from Simcapture
type ForbiddenError = {
  errorCode: 403;
  errorName: 'FORBIDDEN';
  traceId: string;
  additionalData?: string;
};
type NotFoundError = {
  errorCode: '404201';
  errorName: 'USER_NOT_FOUND';
  traceId: string;
};
type InternalServerError = {
  error: 'Internal Server Error';
  message: 'Internal Server Error';
  statusCode: 500;
  traceId: string;
};
type RequestResetPasswordError =
  ForbiddenError | NotFoundError | InternalServerError;
type RequestResetPasswordSuccess = { success: true };
type RequestResetPasswordResult =
  RequestResetPasswordError | RequestResetPasswordSuccess;

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const identifier = formData.get('identifier')?.toString();
  const origin = request.headers.get('origin') ?? '';
  const actionName = formData.get('actionName')?.toString();

  if (actionName === CLOSE_ACTION) {
    return json({ error: undefined });
  }

  const simcapture = createMinimalClient({ request });
  const result: RequestResetPasswordResult = await simcapture.post(
    `/lift/admin/request-reset-password`,
    { identifier },
    {
      label: 'request_reset_password',
      // will InternalServerError if origin header missing
      headers: { origin },
    }
  );

  if ('success' in result) {
    return json({ success: true });
  }

  const errorName = 'errorName' in result ? result.errorName : 'UNKNOWN_ERROR';
  return json({
    error: errorName,
  });
};

const FormAlert = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();

  return (
    <Alert
      sx={{
        background: COLORS.warning_100,
        color: COLORS.warning_700,
        padding: theme.spacing(1.5, 2),
        ...theme.typography['component-bold-s'],
        mt: theme.spacing(2),
        mb: theme.spacing(4),
        [`& .${alertClasses.icon}`]: {
          color: COLORS.warning_700,
          p: 0,
        },
        [`& .${alertClasses.action}`]: {
          p: 0,
        },
        [`& .${alertClasses.message}`]: {
          p: 0,
        },
      }}
      action={
        <Form>
          <IconButton
            aria-label="Close"
            type="submit"
            sx={{
              color: COLORS.warning_500,
              p: 0,
            }}
          >
            <input hidden={true} name="actionName" value={CLOSE_ACTION} />
            <CloseIcon sx={{ fontSize: '22px' }} />
          </IconButton>
        </Form>
      }
      icon={<WarningAmberOutlinedIcon />}
    >
      {children}
    </Alert>
  );
};

export default function ForgotPassword() {
  const actionData = useActionData<typeof action>();

  const theme = useTheme();

  const isError = actionData && 'error' in actionData;
  const isParticipantError = isError && actionData.error === 'FORBIDDEN';
  const actionButtonsDisabled = useNavigation().state !== 'idle';

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      {actionData ? (
        isParticipantError ? (
          <FormAlert>
            You are not able to reset your password. Please reach out to a
            facilitator for help signing in.
          </FormAlert>
        ) : (
          <FormAlert>
            If there is a user associated with this username or email, a reset
            link will be sent shortly.
          </FormAlert>
        )
      ) : null}
      <Stack
        sx={{
          alignItems: 'flex-start',
          flexDirection: 'column',
          paddingY: 1,
        }}
      >
        <TextButton
          text="Back to login"
          variant="tertiary"
          size="small"
          icon={<ArrowBackIcon />}
          href="/login"
        />
      </Stack>
      <Stack>
        <Typography
          component="h1"
          variant="component-bold-l"
          sx={{
            mb: theme.spacing(1),
          }}
        >
          Reset password
        </Typography>

        <Typography
          variant="component-regular-m"
          sx={{
            mb: 1.5,
          }}
        >
          Enter your username or email address to receive a link to reset your
          password.
        </Typography>
      </Stack>
      <Form method="post" id="forgot-password" replace>
        <Stack>
          <InputLabel htmlFor="identifier">
            <Typography variant="component-regular-xxs">
              Username or email address
            </Typography>
          </InputLabel>
          <TextField
            required
            fullWidth
            id="identifier"
            name="identifier"
            sx={{ backgroundColor: 'white' }}
            autoComplete="username"
            autoFocus
            placeholder="e.g. annie@laerdal.com"
          />
        </Stack>
        <Stack
          sx={{
            marginTop: 2,
          }}
        >
          <Stack
            direction="column"
            sx={{
              alignItems: 'stretch',
            }}
          >
            <TextButton
              variant="primary"
              text="Reset password"
              disabled={actionButtonsDisabled}
              type="submit"
              form="forgot-password"
            />
          </Stack>
        </Stack>
      </Form>
    </Stack>
  );
}
